import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Copy,
  Download,
  Eraser,
  Loader2,
  Minus,
  MousePointer2,
  Pencil,
  Redo2,
  Share2,
  Square,
  StickyNote,
  Circle,
  ArrowRight,
  Type,
  Undo2,
} from 'lucide-react';
import WhiteboardCanvas from './components/WhiteboardCanvas.tsx';
import {
  getWhiteboard,
  updateWhiteboard,
} from './services/whiteboards.service.ts';
import type { Whiteboard, WhiteboardElement, WhiteboardTool } from './types/whiteboard.types.ts';
import { createThumbnail, renderWhiteboard } from './utils/whiteboardCanvas.ts';

const TOOLS: Array<{ id: WhiteboardTool; icon: React.ElementType; label: string }> = [
  { id: 'select', icon: MousePointer2, label: 'Seç' },
  { id: 'pen', icon: Pencil, label: 'Kalem' },
  { id: 'eraser', icon: Eraser, label: 'Silgi' },
  { id: 'rectangle', icon: Square, label: 'Dikdörtgen' },
  { id: 'ellipse', icon: Circle, label: 'Elips' },
  { id: 'line', icon: Minus, label: 'Çizgi' },
  { id: 'arrow', icon: ArrowRight, label: 'Ok' },
  { id: 'text', icon: Type, label: 'Metin' },
  { id: 'sticky', icon: StickyNote, label: 'Yapışkan not' },
];

const COLORS = ['#111827', '#dc2626', '#ea580c', '#ca8a04', '#16a34a', '#2563eb', '#7c3aed', '#db2777'];

const WhiteboardEditorPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [board, setBoard] = useState<Whiteboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [tool, setTool] = useState<WhiteboardTool>('pen');
  const [color, setColor] = useState('#111827');
  const [strokeWidth, setStrokeWidth] = useState(3);
  const [history, setHistory] = useState<WhiteboardElement[][]>([]);
  const [future, setFuture] = useState<WhiteboardElement[][]>([]);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!id) return;
    void (async () => {
      const data = await getWhiteboard(id);
      if (!data) {
        navigate('/products/whiteboards', { replace: true });
        return;
      }
      setBoard(data);
      setLoading(false);
    })();
  }, [id, navigate]);

  const persist = useCallback(
    async (elements: WhiteboardElement[], title?: string) => {
      if (!board) return;
      setSaving(true);
      try {
        const thumbnail = createThumbnail(elements, board.backgroundColor);
        const updated = await updateWhiteboard(board.id, {
          elements,
          thumbnail,
          ...(title !== undefined ? { title } : {}),
        });
        if (updated) setBoard(updated);
      } finally {
        setSaving(false);
      }
    },
    [board]
  );

  const handleElementsChange = (elements: WhiteboardElement[]) => {
    if (!board) return;
    setHistory((h) => [...h, board.elements]);
    setFuture([]);
    setBoard({ ...board, elements });
    void persist(elements);
  };

  const undo = () => {
    if (!board || history.length === 0) return;
    const prev = history[history.length - 1];
    setFuture((f) => [board.elements, ...f]);
    setHistory((h) => h.slice(0, -1));
    setBoard({ ...board, elements: prev });
    void persist(prev);
  };

  const redo = () => {
    if (!board || future.length === 0) return;
    const next = future[0];
    setHistory((h) => [...h, board.elements]);
    setFuture((f) => f.slice(1));
    setBoard({ ...board, elements: next });
    void persist(next);
  };

  const handleExport = () => {
    if (!board) return;
    const canvas = document.createElement('canvas');
    canvas.width = 1600;
    canvas.height = 900;
    renderWhiteboard(canvas, board.elements, board.backgroundColor);
    const link = document.createElement('a');
    link.download = `${board.title.replace(/\s+/g, '_')}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/products/whiteboards/${id}`;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleTitleBlur = (title: string) => {
    if (!board || title.trim() === board.title) return;
    void persist(board.elements, title.trim() || 'Adsız beyaz tahta');
  };

  if (loading || !board) {
    return (
      <div className="flex items-center justify-center py-24 text-gray-300 gap-3">
        <Loader2 className="w-5 h-5 animate-spin" />
        Beyaz tahta yükleniyor...
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-[calc(100vh-7.5rem)] -mt-2 lg:-mt-2">
      <div className="flex flex-col flex-1 min-h-0 bg-slate-800/50 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden">
        {/* Başlık + araçlar — mobilde iki satır, masaüstünde tek kompakt satır */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-3 px-4 pr-6 py-2 border-b border-white/10 bg-slate-900/50">
          <div className="flex items-center gap-2 min-w-0 shrink-0 lg:max-w-[220px] xl:max-w-[280px]">
            <Link
              to="/products/whiteboards"
              className="p-1 rounded-lg hover:bg-white/10 text-gray-300 hover:text-white transition shrink-0"
              title="Geri"
            >
              <ArrowLeft className="w-4 h-4" />
            </Link>

            <input
              key={board.id}
              defaultValue={board.title}
              onBlur={(e) => handleTitleBlur(e.target.value)}
              className="flex-1 min-w-0 bg-transparent text-white font-semibold text-sm focus:outline-none border-b border-transparent focus:border-purple-500 truncate"
            />
          </div>

          <div className="flex items-center gap-1.5 flex-1 min-w-0 overflow-x-auto pb-0.5 lg:pb-0 lg:justify-center">
            <div className="flex items-center gap-0.5 shrink-0">
              {TOOLS.map((t) => {
                const Icon = t.icon;
                const active = tool === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTool(t.id)}
                    title={t.label}
                    className={`p-1 rounded-lg transition ${
                      active ? 'bg-purple-600 text-white' : 'text-gray-300 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                  </button>
                );
              })}
            </div>

            <div className="w-px h-5 bg-white/10 shrink-0 mx-0.5" />

            <div className="flex items-center gap-1 shrink-0">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`w-4 h-4 rounded-full border-2 transition ${
                    color === c ? 'border-white scale-110' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: c }}
                  title={c}
                />
              ))}
            </div>

            <div className="w-px h-5 bg-white/10 shrink-0 mx-0.5" />

            <input
              type="range"
              min={1}
              max={12}
              value={strokeWidth}
              onChange={(e) => setStrokeWidth(Number(e.target.value))}
              className="w-14 accent-purple-500 shrink-0"
              title="Kalınlık"
            />

            <button
              type="button"
              onClick={undo}
              disabled={history.length === 0}
              className="p-1 rounded-lg text-gray-300 hover:bg-white/10 disabled:opacity-40 shrink-0"
              title="Geri al"
            >
              <Undo2 className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={redo}
              disabled={future.length === 0}
              className="p-1 rounded-lg text-gray-300 hover:bg-white/10 disabled:opacity-40 shrink-0"
              title="Yinele"
            >
              <Redo2 className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="flex items-center gap-2 shrink-0 lg:ml-auto">
            {saving && <span className="text-xs text-gray-400 hidden sm:inline">Kaydediliyor...</span>}
            <button
              type="button"
              onClick={handleShare}
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs sm:text-sm"
            >
              {copied ? <Copy className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">{copied ? 'Kopyalandı' : 'Paylaş'}</span>
            </button>
            <button
              type="button"
              onClick={handleExport}
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-xs sm:text-sm"
            >
              <Download className="w-3.5 h-3.5" />
              PNG
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 p-3 pl-4 pr-6 pb-4">
          <WhiteboardCanvas
            elements={board.elements}
            backgroundColor={board.backgroundColor}
            tool={tool}
            color={color}
            strokeWidth={strokeWidth}
            onChange={handleElementsChange}
          />
        </div>
      </div>
    </div>
  );
};

export default WhiteboardEditorPage;
